/**
 * Dynamic module page that resolves a module slug from the URL,
 * determines whether to render a workflow or custom interface,
 * and delegates to the appropriate renderer.
 */
import { Suspense } from "react";
import { Navigate, useParams } from "react-router-dom";

import { WorkflowModulePage } from "./WorkflowModulePage";

import { trpc } from "@/lib/trpc";
import { getCustomModuleComponent, type CustomModuleProps } from "@/modules/registry";
import { useAuthStore } from "@/stores/auth";

/**
 * Loading spinner shown while module data or lazy components load.
 */
function ModuleLoadingState() {
	return (
		<div className="flex min-h-[400px] items-center justify-center">
			<div className="flex flex-col items-center space-y-4">
				<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
				<p className="text-sm text-muted-foreground">Loading module...</p>
			</div>
		</div>
	);
}

/**
 * Error state shown when a custom interface componentKey is not in the registry.
 * @param props - component props
 * @param props.componentKey - the unregistered component key
 */
function UnregisteredComponentError({ componentKey }: { componentKey: string }) {
	return (
		<div className="p-6">
			<div className="rounded-lg border border-destructive bg-destructive/10 p-6 text-center">
				<h2 className="mb-2 text-lg font-semibold text-red-400">
					Module Component Not Found
				</h2>
				<p className="text-sm text-muted-foreground">
					The custom interface component &quot;{componentKey}&quot; is not registered.
					Contact your platform administrator.
				</p>
			</div>
		</div>
	);
}

/**
 * Main module page component.
 * Reads moduleSlug from URL params, fetches module data, and delegates to
 * WorkflowModulePage (for workflow modules) or a lazy custom interface component.
 */
export function ModulePage() {
	const { moduleSlug } = useParams<{ moduleSlug: string }>();
	const { user, session, activeTeam, accounts, permissions } = useAuthStore();

	// Fetch module by slug — access control is enforced server-side
	const moduleQuery = trpc.modules.getBySlug.useQuery(
		{ slug: moduleSlug ?? "" },
		{ enabled: !!moduleSlug }
	);

	if (moduleQuery.isLoading) {
		return <ModuleLoadingState />;
	}

	// Module not found, not accessible, or slug missing — go home via the resolver
	if (moduleQuery.error || !moduleQuery.data) {
		return <Navigate to="/" replace />;
	}

	const module = moduleQuery.data;

	// Route based on module type
	if (module.moduleType === "custom_interface" && module.componentKey) {
		const CustomComponent = getCustomModuleComponent(module.componentKey);

		if (!CustomComponent) {
			return <UnregisteredComponentError componentKey={module.componentKey} />;
		}

		const props: CustomModuleProps = {
			moduleId: module.id,
			moduleSlug: module.slug,
			basePath: `/m/${module.slug}`,
			context:
				user && session && activeTeam
					? { user, session, activeTeam, accounts, permissions }
					: undefined,
			trpc,
		};

		return (
			<Suspense fallback={<ModuleLoadingState />}>
				<CustomComponent {...props} />
			</Suspense>
		);
	}

	// Default: workflow module
	return <WorkflowModulePage module={module} />;
}
